import { z } from 'zod/v4';

/*
 * Locale values, split by audience.
 *
 * APPLICATION_LOCALES — languages the Recovr app itself is translated into: the user's own
 * interface, the debtor portal, third parties, the backoffice.
 *
 * COMMUNICATION_LOCALES — languages a debtor can be written to: debtors, contact people, the
 * company's default language, template bodies. Which of these a given company actually uses is a
 * per-tenant subset, read from the communication locales endpoint.
 *
 * The same two names carry the same meaning in the backend's Constants::Locales.
 */

export const APPLICATION_LOCALES = ['fr', 'en', 'nl'] as const;

export type ApplicationLocale = (typeof APPLICATION_LOCALES)[number];

export const ApplicationLocaleSchema = z.enum([...APPLICATION_LOCALES]);

// Not exported: callers want COMMUNICATION_LANGUAGES or the schema, and the enabled subset
// comes from the API rather than this list.
const COMMUNICATION_LOCALES = ['fr', 'en', 'nl', 'de'] as const;

export type CommunicationLocale = (typeof COMMUNICATION_LOCALES)[number];

export const CommunicationLocaleSchema = z.enum([...COMMUNICATION_LOCALES]);

// `satisfies` rather than a type annotation: adding a language to either set above fails here
// until it has a name, while the keys stay literal for the lists below.
const LANGUAGE_NAMES = {
  fr: 'Français',
  en: 'English',
  nl: 'Nederlands',
  de: 'Deutsch',
} satisfies Record<ApplicationLocale | CommunicationLocale, string>;

const LANGUAGE_NAMES_BY_CODE: Record<string, string> = LANGUAGE_NAMES;

// Takes a plain string: a locale read from the API is not narrowed, and a language this build does
// not know should still render as something rather than blank.
export const languageName = (locale: string): string => LANGUAGE_NAMES_BY_CODE[locale] ?? locale;

export const APPLICATION_LANGUAGES: Array<{ description: string; value: ApplicationLocale }> =
  APPLICATION_LOCALES.map(value => ({ value, description: LANGUAGE_NAMES[value] }));

export const COMMUNICATION_LANGUAGES: Array<{ description: string; value: CommunicationLocale }> =
  COMMUNICATION_LOCALES.map(value => ({ value, description: LANGUAGE_NAMES[value] }));
